/**
 * Face-shape classifier driven by MediaPipe Face Landmarker landmarks.
 *
 * The AR mirror's contour step uses the detected shape to choose
 * between six pre-authored contour stroke sets (see
 * `CONTOUR_BY_SHAPE` in faceLandmarks.js). Classification runs
 * client-side only — landmarks never leave the device.
 *
 * Standard beauty-industry face shapes recognised:
 *   • oval     — balanced, narrower forehead + jaw than cheekbones
 *   • round    — width ≈ length, soft jaw, equal widths
 *   • heart    — broad forehead, narrow pointed chin
 *   • long     — face length noticeably greater than width
 *   • square   — strong jaw, equal width forehead/cheekbones/jaw
 *   • diamond  — cheekbones widest, narrower forehead + jaw
 *
 * We deliberately return a confidence band so the caller can decide
 * whether to trust the classification. Confidence rests on:
 *   (a) how cleanly the ratios land in one bucket vs straddling two
 *   (b) whether the user's face is approximately frontal (yaw ≈ 0,
 *       measured from nose-tip x vs face-oval x midpoint).
 */

// MediaPipe Face Mesh landmark indices used for shape classification.
// Source of truth: MediaPipe Face Landmarker 478-point mesh.
const IDX = {
  foreheadTop: 10,       // forehead centre
  chinTip: 152,          // chin bottom
  templeR: 103,          // upper-right temple corner (in FACE_OVAL)
  templeL: 332,          // upper-left temple corner (in FACE_OVAL)
  cheekbonePeakR: 234,   // widest right zygomatic arch point
  cheekbonePeakL: 454,   // widest left zygomatic arch point
  jawAngleR: 172,        // right gonion (jaw corner)
  jawAngleL: 397,        // left gonion (jaw corner)
  noseTip: 1,            // used for yaw / frontal check
};

const dist = (a, b) => {
  if (!a || !b) return 0;
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.hypot(dx, dy);
};

/**
 * Compute the five scalar ratios that drive classification. Uses
 * normalised landmark coordinates (0..1 in the raw video frame) so
 * the ratios are resolution-independent.
 */
export const computeFaceShapeMetrics = (landmarks) => {
  if (!landmarks || landmarks.length < 478) return null;
  const faceLength = dist(landmarks[IDX.foreheadTop], landmarks[IDX.chinTip]);
  const cheekboneWidth = dist(
    landmarks[IDX.cheekbonePeakR],
    landmarks[IDX.cheekbonePeakL],
  );
  const foreheadWidth = dist(
    landmarks[IDX.templeR],
    landmarks[IDX.templeL],
  );
  const jawWidth = dist(
    landmarks[IDX.jawAngleR],
    landmarks[IDX.jawAngleL],
  );
  if (faceLength === 0 || cheekboneWidth === 0) return null;

  // Yaw estimate: where does the nose tip sit relative to the
  // cheekbone midpoint? Strongly non-frontal faces give unreliable
  // width ratios. Expressed as [0..1] of cheekbone width.
  const midCheekX =
    (landmarks[IDX.cheekbonePeakR].x + landmarks[IDX.cheekbonePeakL].x) / 2;
  const yaw = Math.abs(landmarks[IDX.noseTip].x - midCheekX) / cheekboneWidth;

  return {
    lengthToWidth: faceLength / cheekboneWidth,
    foreheadToCheek: foreheadWidth / cheekboneWidth,
    jawToCheek: jawWidth / cheekboneWidth,
    foreheadToJaw: jawWidth > 0 ? foreheadWidth / jawWidth : 1,
    yaw,
    // Raw values exposed for tests / display
    faceLength,
    cheekboneWidth,
    foreheadWidth,
    jawWidth,
  };
};

/**
 * Map a metrics object to one of the six face-shape buckets.
 * Returns { shape, confidence: "high" | "medium" | "low" }.
 *
 * Heuristics rest on published face-shape rules commonly used in
 * contour tutorials. They're applied in order — first match wins —
 * so the more discriminating shapes (long, heart, diamond) come
 * before the fallback buckets (round, square, oval).
 */
export const classifyFaceShape = (landmarks) => {
  const m = computeFaceShapeMetrics(landmarks);
  if (!m) return { shape: "oval", confidence: "low", metrics: null };

  // Strong yaw → we can't trust widths. Return oval as the safest
  // default (it's the contour set closest to neutral/balanced).
  if (m.yaw > 0.22) {
    return { shape: "oval", confidence: "low", metrics: m };
  }

  const { lengthToWidth, foreheadToCheek, jawToCheek, foreheadToJaw } = m;

  // Long — the face is noticeably taller than wide. This dominates
  // because contour for long faces is about shortening, regardless
  // of where the widest point sits.
  if (lengthToWidth > 1.45) {
    return { shape: "long", confidence: "high", metrics: m };
  }

  // Heart — broad forehead, narrow jaw. foreheadToJaw > 1.15 means
  // the forehead is meaningfully wider than the jaw.
  if (foreheadToJaw > 1.15 && jawToCheek < 0.82) {
    return { shape: "heart", confidence: "high", metrics: m };
  }

  // Diamond — cheekbones are the widest point by a clear margin.
  // Both forehead and jaw sit meaningfully below the cheek width.
  if (foreheadToCheek < 0.82 && jawToCheek < 0.82) {
    return { shape: "diamond", confidence: "high", metrics: m };
  }

  // Round — length ≈ width, and all three horizontal widths are
  // broadly similar (within 12%).
  const widthSpread = Math.max(foreheadToCheek, jawToCheek, 1) /
    Math.min(foreheadToCheek, jawToCheek, 1);
  if (lengthToWidth < 1.2 && widthSpread < 1.15) {
    return { shape: "round", confidence: "high", metrics: m };
  }

  // Square — strong jaw (jawToCheek > 0.92) and length not too long.
  // Forehead and jaw are roughly equal (|foreheadToJaw - 1| < 0.12).
  if (
    jawToCheek > 0.92 &&
    lengthToWidth < 1.4 &&
    Math.abs(foreheadToJaw - 1) < 0.14
  ) {
    return { shape: "square", confidence: "high", metrics: m };
  }

  // Oval — balanced default. Confidence is "medium" when we arrived
  // here because no other bucket matched rather than because the
  // face is a textbook oval.
  const looksLikeTextbookOval =
    lengthToWidth >= 1.2 &&
    lengthToWidth <= 1.45 &&
    foreheadToCheek >= 0.82 &&
    foreheadToCheek <= 0.98 &&
    jawToCheek >= 0.72 &&
    jawToCheek <= 0.9;
  return {
    shape: "oval",
    confidence: looksLikeTextbookOval ? "high" : "medium",
    metrics: m,
  };
};

/**
 * Rolling-majority smoother — avoids flicker when the per-frame
 * classifier drifts between two adjacent buckets. Usage:
 *
 *   const smoother = createShapeSmoother();
 *   // optionally seed from a previously-saved value so the AR
 *   // mirror has SOMETHING to render on frame 1:
 *   smoother.seed(savedFaceShape);
 *   // inside rAF loop:
 *   const stable = smoother.push(classifyFaceShape(landmarks));
 *   // stable is the seed (or null) for the first few frames, then
 *   // locks on to whatever the classifier agrees about.
 *
 * Buffer size of 10 with a 5/10 majority threshold means a stable
 * shape gets adopted after the classifier agrees on it for half
 * the recent samples. High-confidence samples are double-weighted.
 * Threshold was 6/10 — bumped down to 5/10 so initial convergence
 * lands ~25% faster (was ~5s at 2Hz, now ~3s) which matters during
 * AR-mirror sessions where the contour zone is recomputed from the
 * smoother output.
 */
export const createShapeSmoother = (size = 10, threshold = 5) => {
  const buffer = [];
  let currentShape = null;

  return {
    /**
     * Seed the smoother with a previously-saved shape so the AR
     * mirror has a sensible default to render before the live
     * classifier has converged. The seed value is treated as one
     * regular-weight sample, so a few high-confidence live readings
     * will quickly override it if the user actually has a different
     * face shape today.
     */
    seed(shape) {
      if (!shape) return;
      currentShape = shape;
      buffer.push(shape);
      while (buffer.length > size) buffer.shift();
    },
    push(result) {
      if (!result || !result.shape) return currentShape;
      const weight = result.confidence === "high" ? 2 : 1;
      for (let i = 0; i < weight; i++) buffer.push(result.shape);
      while (buffer.length > size) buffer.shift();

      // Mode of the buffer
      const counts = Object.create(null);
      for (const s of buffer) counts[s] = (counts[s] || 0) + 1;
      let winner = null;
      let winnerCount = 0;
      for (const k of Object.keys(counts)) {
        if (counts[k] > winnerCount) {
          winner = k;
          winnerCount = counts[k];
        }
      }
      if (winnerCount >= threshold) currentShape = winner;
      return currentShape;
    },
    reset() {
      buffer.length = 0;
      currentShape = null;
    },
    current() {
      return currentShape;
    },
  };
};

export const FACE_SHAPE_LABELS = {
  oval: "Oval",
  round: "Round",
  heart: "Heart",
  long: "Long",
  square: "Square",
  diamond: "Diamond",
};
